import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../../../api";

interface Answer {
  text?: string;
  imageId: number;
  is_right: boolean;
}

interface QuizImageMultipleData {
  question_text?: string;
  explanation_true_text?: string;
  explanation_false_text?: string;
  has_multiple_answers?: boolean;
  list_answers: Answer[];
}

interface AnswerWithImage extends Answer {
  imageUrl?: string;
  index: number;
}

interface QuizImageMultipleDetailProps {
  data: {
    list_selected_answers_indexes?: number[];
  };
  contentId?: number;
}

const QuizImageMultipleDetail = ({ data, contentId }: QuizImageMultipleDetailProps) => {
  const { t } = useTranslation();
  const [quizData, setQuizData] = useState<QuizImageMultipleData | null>(null);
  const [answersWithImages, setAnswersWithImages] = useState<AnswerWithImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedContentIdRef = useRef<number | null>(null);

  const selectedIndexes = data?.list_selected_answers_indexes || [];

  // Fetch quiz data and images
  useEffect(() => {
    const fetchData = async () => {
      if (!contentId) return;

      // Prevent duplicate fetches
      if (fetchedContentIdRef.current === contentId) return;
      fetchedContentIdRef.current = contentId;

      setIsLoading(true);

      try {
        // Fetch quiz content
        const response = await axios.get(
          `${apiUrls.base}/api/content-quizz-image-multiples?filters[content][id][$eq]=${contentId}`,
        );

        const results = response.data?.data || response.data;
        const quizContent = Array.isArray(results) ? results[0] : results;

        if (quizContent) {
          const attrs = quizContent.attributes || quizContent;
          const listAnswers: Answer[] = attrs.list_answers || [];

          setQuizData({
            question_text: attrs.question_text || "",
            explanation_true_text: attrs.explanation_true_text || "",
            explanation_false_text: attrs.explanation_false_text || "",
            has_multiple_answers: attrs.has_multiple_answers,
            list_answers: listAnswers,
          });

          // Fetch images for each answer
          const imageIds = listAnswers.map((a) => a.imageId).filter((id) => id);
          if (imageIds.length > 0) {
            const imagePromises = imageIds.map((id) =>
              axios
                .get(`${apiUrls.medias.all}/${id}`)
                .then((res) => ({
                  id,
                  url: res.data?.data?.attributes?.s3_url || res.data?.s3_url || null,
                }))
                .catch(() => ({ id, url: null })),
            );

            const imageResults = await Promise.all(imagePromises);
            const imageMap = new Map(imageResults.map((r) => [r.id, r.url]));

            const answersWithImg: AnswerWithImage[] = listAnswers.map((answer, index) => ({
              ...answer,
              index,
              imageUrl: imageMap.get(answer.imageId) || undefined,
            }));

            setAnswersWithImages(answersWithImg);
          }
        }
      } catch (err) {
        console.error("Error fetching quiz image multiple content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [contentId]);

  // Determine if user got it right (selected all correct answers and no wrong ones)
  const isCorrect =
    answersWithImages.length > 0 &&
    answersWithImages.every((answer) => {
      const wasSelected = selectedIndexes.includes(answer.index);
      // Correct if: selected and is_right, or not selected and not is_right
      return wasSelected === answer.is_right;
    });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!quizData || answersWithImages.length === 0) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  const explanation = isCorrect ? quizData.explanation_true_text : quizData.explanation_false_text;

  return (
    <div className="h-full flex flex-col overflow-y-auto">
      {/* Question */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          {quizData.question_text && (
            <p className="text-sm font-medium text-gray-700">{quizData.question_text}</p>
          )}
          {quizData.has_multiple_answers && (
            <span className="text-xs text-gray-400 italic">
              ({t("statsNew.interaction.quiz.multipleChoice")})
            </span>
          )}
        </div>
      </div>

      {/* Images grid */}
      <div className="border-2 border-teal-200 rounded-lg p-3 mb-4">
        <div className="grid grid-cols-5 gap-2">
          {answersWithImages.map((answer) => {
            const wasSelected = selectedIndexes.includes(answer.index);
            const isRight = answer.is_right;

            // Determine status
            let status: "correct" | "wrong" | "missed" | "neutral";
            if (wasSelected && isRight) {
              status = "correct"; // User selected a correct answer
            } else if (wasSelected && !isRight) {
              status = "wrong"; // User selected a wrong answer
            } else if (!wasSelected && isRight) {
              status = "missed"; // User missed a correct answer
            } else {
              status = "neutral"; // User correctly didn't select a wrong answer
            }

            return (
              <div
                key={answer.index}
                className={`relative rounded-lg border-2 ${
                  status === "correct"
                    ? "border-green-500"
                    : status === "wrong"
                    ? "border-red-500"
                    : status === "missed"
                    ? "border-yellow-500"
                    : "border-gray-200"
                }`}
              >
                {/* Image */}
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  {answer.imageUrl ? (
                    <img
                      src={answer.imageUrl}
                      alt={answer.text || `Image ${answer.index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <svg
                        className="h-8 w-8"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Status indicator */}
                {status !== "neutral" && (
                  <div
                    className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                      status === "correct"
                        ? "bg-green-500"
                        : status === "wrong"
                        ? "bg-red-500"
                        : "bg-yellow-500"
                    }`}
                    title={
                      status === "correct"
                        ? t("statsNew.interaction.quiz.tooltipCorrect")
                        : status === "wrong"
                        ? t("statsNew.interaction.quiz.tooltipWrong")
                        : t("statsNew.interaction.quiz.tooltipMissed")
                    }
                  >
                    {status === "correct" ? (
                      <svg
                        className="h-3 w-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : status === "wrong" ? (
                      <svg
                        className="h-3 w-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    ) : (
                      <span className="text-white text-xs font-bold">!</span>
                    )}
                  </div>
                )}

                {/* Text label if available */}
                {answer.text && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                    <p className="text-xs text-white truncate">{answer.text}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
            <svg
              className="h-3 w-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <span className="text-gray-600">{t("statsNew.interaction.quiz.tooltipCorrect")}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
            <svg
              className="h-3 w-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <span className="text-gray-600">{t("statsNew.interaction.quiz.tooltipWrong")}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-full bg-yellow-500 flex items-center justify-center">
            <span className="text-white text-xs font-bold">!</span>
          </div>
          <span className="text-gray-600">{t("statsNew.interaction.quiz.tooltipMissed")}</span>
        </div>
      </div>

      {/* Explanation */}
      {explanation && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">
            {t("statsNew.interaction.explanation")}
          </p>
          <div className={`p-3 rounded-lg ${isCorrect ? "bg-green-50" : "bg-red-50"}`}>
            <p className={`text-sm ${isCorrect ? "text-green-700" : "text-red-700"}`}>
              {explanation}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizImageMultipleDetail;
